// src/routes/cr/CRBattles.tsx
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useBattlesQuery, useProfileMeQuery } from '@/services/qbotApi';
import { BattlesListView } from '@/components/cr/BattlesListView';
import { SectionCard } from '@/components/hub/SectionCard';
import { Segmented } from '@/components/common/Segmented';
import { BattlesStats } from '@/components/cr/BattlesStats';
import { cn } from '@/lib/cn';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setBattlesView } from '@/store/core.slice';
import type { CRBattle, QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

type BattlesView = 'list' | 'stats';

/**
 * Battles surface — a segmented List | Stats view over ONE shared 25-battle
 * window. Tag comes from /profile/me (no tag in the URL — the user wants their
 * own data); no linked player → redirect to /cr/link.
 *
 * Refresh sends `fresh=true` (a distinct RTK cache key) so qbot bypasses its
 * cache + the qCrC collector and reads the log directly from Supercell. Both
 * views read the same query, so a refresh updates List and Stats together.
 */
export function CRBattles() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const view = useAppSelector((s) => s.core.battlesView);
  const [fresh, setFresh] = useState(false);

  const { data: profile, isLoading: profileLoading, error: profileError } = useProfileMeQuery();
  const tag = profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';

  const { data, isFetching, error, refetch } = useBattlesQuery(
    { tag, page: 1, pageSize: 25, fresh },
    { skip: !tag },
  );
  const battles: CRBattle[] = data?.battles ?? [];

  const onRefresh = () => {
    if (isFetching) return;
    haptics.impact('light');
    if (fresh) refetch(); else setFresh(true);
  };

  if (profileLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('battles.loading')}</div>;
  }
  if (profileError) {
    const code = isQbotApiError(profileError) ? profileError.data?.error?.code : undefined;
    if (code === 'NOT_LINKED' || code === 'NOT_FOUND') return <Navigate to="/cr/link" replace />;
  }

  const onRowClick = (battle: CRBattle, absIdx: number) => {
    haptics.selection();
    navigate(`/cr/battles/${absIdx}`, { state: { battle, playerTag: tag } });
  };

  const switchView = (next: BattlesView) => {
    if (next === view) return;
    haptics.selection();
    dispatch(setBattlesView(next));
  };

  return (
    // NON scorre piu' questo contenitore: prima scorreva tutto insieme, quindi
    // la card di fondo scivolava via col contenuto e il suo bordo superiore
    // usciva dallo schermo — sembrava tagliata. Ora la card resta FERMA e
    // scorre solo cio' che sta dentro, quindi i suoi angoli arrotondati sono
    // sempre visibili e il contenuto sembra scorrere dentro una finestra.
    // py-1 e non py-4: incorporata nella Hub, il pannello ha gia' il suo
    // spazio sopra e sotto, e i due si sommavano staccando la card dalla barra
    // molto piu' delle altre sezioni. Da rotta autonoma (/cr/battles) resta un
    // filo d'aria, sufficiente perche' la chrome sopra ne porta gia'.
    <div className="flex h-full flex-col gap-3 overflow-hidden px-4 py-1">
      {/* Intestazione: a sinistra COSA guardo (cronologia o statistiche), a
          destra l'unica azione (aggiorna). Due gruppi separati e non una fila
          indistinta di bottoni: scegliere una vista e ricaricare i dati sono
          gesti di natura diversa, e mescolarli faceva sembrare "Aggiorna" una
          terza vista. */}
      <header className="flex shrink-0 items-center gap-2">
        <Segmented
          className="flex-1"
          value={view}
          onChange={switchView}
          options={(['list', 'stats'] as const).map((v) => ({
            value: v,
            label: t(`battles.seg.${v}`, { defaultValue: v === 'list' ? 'Battles' : 'Stats' }),
          }))}
        />
        {/* Contorno invece di superficie piena: è un'azione secondaria e non
            deve competere con la vista selezionata, che è piena e ciano. */}
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          aria-label={t('battles.refresh')}
          className="flex shrink-0 items-center gap-1.5 rounded-2xl px-4 py-2.5 text-sm font-bold text-fg ring-1 ring-white/20 transition-colors active:bg-white/5 disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          {t('battles.refresh')}
        </button>
      </header>

      {!!error && <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">{t('battles.error')}</p>}

      {/* Tutto il contenuto vive dentro UNA superficie opaca: i filtri e le
          righe erano appoggiati direttamente sulla texture a scaglie, che
          passava dietro il testo. */}
      {/* La card prende l'altezza rimasta e CLIPPA; lo scorrimento vive nel div
          interno. `flush` toglie il padding alla card e lo sposta sullo
          scroller: cosi' il contenuto scorre fin sotto il bordo arrotondato
          invece di fermarsi a una fascia vuota. */}
      {/* NIENTE flex-1: la card CONTIENE, non riempie. Con flex-1 arrivava in
          fondo allo schermo anche con due righe dentro, e il vuoto sotto
          l'ultima riga sembrava contenuto mancante. Senza, prende l'altezza del
          suo contenuto e si ferma; quando il contenuto supera lo spazio
          disponibile il flex la fa rientrare (`flex: 0 1 auto`) e a quel punto
          scorre l'interno. */}
      <SectionCard flush className="flex min-h-0 flex-col overflow-hidden">
        <div className="min-h-0 overflow-y-auto overscroll-contain p-3 [touch-action:pan-y]">
          {isFetching && battles.length === 0 ? (
            <div className="px-4 py-6 text-sm text-fg-muted">{t('battles.loading')}</div>
          ) : view === 'list' ? (
            <BattlesListView battles={battles} playerTag={tag} onRowClick={onRowClick} />
          ) : (
            <BattlesStats battles={battles} playerTag={tag} />
          )}
        </div>
      </SectionCard>
    </div>
  );
}
